/**
 * 광고 목록 페이지
 *
 * 광고를 BANNER(상단 배너)·BOX(하단 박스) 두 섹션으로 분리해 sortOrder 순으로 표시한다.
 * 같은 sortOrder가 중복되면 카드에 경고 배지를 노출한다.
 * HomePreviewModal을 통해 실제 웹뷰 홈 화면에서 광고가 어떻게 보이는지 미리볼 수 있다.
 */
import { useQuery } from "@tanstack/react-query";
import { adverService } from "../../services/adverService";
import { Link } from "react-router-dom";
import { useState } from "react";
import { Adver } from "../../types/adver";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/swiper.css";
import LoadingSpinner from "../../components/ui/LoadingSpinner";
import ErrorAlert from "../../components/ui/ErrorAlert";
import StatsCard from "../../components/ui/StatsCard";
import { formatDate } from "../../utils/format";

const BANNER_COLORS = ["#5B21B6", "#10b981", "#3b82f6", "#f59e0b", "#ef4444"];
const BOX_COLORS = ["#3b82f6", "#10b981", "#ef4444"];

function AdverCard({
  adver,
  rank,
  isDuplicate,
}: {
  adver: Adver;
  rank: number;
  isDuplicate: boolean;
}) {
  return (
    <Link
      to={`/admin/adver/${adver.id}`}
      state={{ adver }}
      className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow group"
    >
      <div className="relative aspect-[2/1] bg-gray-100">
        <img
          src={adver.imageUrl}
          alt={adver.title}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.style.display = "none";
          }}
        />
        {!adver.isVisible && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <span className="px-4 py-2 bg-gray-800 text-white text-sm font-bold rounded-full">
              <i className="fas fa-pause-circle mr-2"></i>비활성
            </span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex gap-2 items-center">
          <span className="w-8 h-8 flex items-center justify-center bg-primary text-white text-sm font-bold rounded-full shadow">
            {rank}
          </span>
          {isDuplicate && (
            <span className="px-2 py-1 bg-marker-orange text-white text-xs font-bold rounded-full">
              순서 중복
            </span>
          )}
        </div>
        {isDuplicate && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 bg-black/50 text-white text-xs rounded-full">
              설정값 {adver.sortOrder}
            </span>
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary transition-colors">
          {adver.title}
        </h3>
        <div className="flex items-center text-sm text-gray-500 mb-3">
          <i className="fas fa-link mr-2"></i>
          <span className="truncate">{adver.linkUrl}</span>
        </div>
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>
            <i className="fas fa-calendar-alt mr-1"></i>
            {formatDate(adver.createdAt)}
          </span>
          {adver.isVisible ? (
            <span className="text-primary font-semibold">
              <i className="fas fa-check-circle mr-1"></i>활성
            </span>
          ) : (
            <span className="text-gray-500 font-semibold">
              <i className="fas fa-pause-circle mr-1"></i>비활성
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

function PreviewBannerSlide({ adver, index }: { adver: Adver; index: number }) {
  return (
    <div className="relative w-full">
      {adver.imageUrl ? (
        <img
          src={adver.imageUrl}
          alt={adver.title}
          className="w-full object-contain"
          style={{ display: "block" }}
        />
      ) : (
        <div
          className="w-full flex items-center justify-center text-white text-sm font-bold"
          style={{
            backgroundColor: BANNER_COLORS[index % BANNER_COLORS.length],
            aspectRatio: "5 / 2",
          }}
        >
          {adver.title}
        </div>
      )}
    </div>
  );
}

function PreviewBoxCard({ adver, index }: { adver: Adver; index: number }) {
  return (
    <div className="rounded-2xl overflow-hidden">
      {adver.imageUrl ? (
        <div className="w-full aspect-square bg-gray-100">
          <img
            src={adver.imageUrl}
            alt={adver.title}
            className="w-full h-full object-contain"
          />
        </div>
      ) : (
        <div
          className="w-full aspect-square flex items-center justify-center text-white text-xs font-bold text-center px-1"
          style={{ backgroundColor: BOX_COLORS[index % BOX_COLORS.length] }}
        >
          {adver.title}
        </div>
      )}
    </div>
  );
}

function HomePreviewModal({
  banners,
  boxes,
  onClose,
}: {
  banners: Adver[];
  boxes: Adver[];
  onClose: () => void;
}) {
  const visibleBanners = banners.filter((a) => a.isVisible);
  const visibleBoxes = boxes.filter((a) => a.isVisible);
  const [slideIndex, setSlideIndex] = useState(0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={onClose}
    >
      <div
        className="relative flex flex-col"
        style={{ width: 390, maxHeight: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <span className="text-white font-bold text-lg">
              웹뷰 홈 미리보기
            </span>
            <p className="text-white/60 text-xs mt-0.5">
              활성 광고만 표시됩니다
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div
          className="relative bg-white rounded-[40px] overflow-hidden shadow-2xl flex-1"
          style={{
            border: "10px solid #1a1a1a",
            boxShadow: "0 0 0 2px #333, 0 30px 60px rgba(0,0,0,0.5)",
          }}
        >
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 z-20 rounded-b-2xl"
            style={{ width: 120, height: 28, backgroundColor: "#1a1a1a" }}
          />

          <div
            className="overflow-y-auto flex flex-col"
            style={{ backgroundColor: "#F8F7F1", height: 680, paddingTop: 36 }}
          >
            {visibleBanners.length > 0 && (
              <div className="relative w-full px-3 pt-3">
                <div
                  className="relative rounded-2xl overflow-hidden"
                  style={{ backgroundColor: "#EEEEEE" }}
                >
                  <Swiper
                    modules={[Autoplay]}
                    spaceBetween={0}
                    slidesPerView={1}
                    autoplay={{ delay: 3000, disableOnInteraction: false }}
                    loop={visibleBanners.length > 1}
                    onSlideChange={(swiper) => setSlideIndex(swiper.realIndex)}
                  >
                    {visibleBanners.map((adver, i) => (
                      <SwiperSlide key={adver.id}>
                        <PreviewBannerSlide adver={adver} index={i} />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                  {visibleBanners.length > 1 && (
                    <div
                      className="absolute bottom-2 right-2 text-white text-[9px] px-1.5 py-0.5 rounded-md font-semibold z-10"
                      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                    >
                      {slideIndex + 1} / {visibleBanners.length}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="px-3 pt-3">
              <h2
                className="text-base font-bold mb-2"
                style={{ color: "#222222" }}
              >
                공지사항
              </h2>
              <div
                className="bg-white rounded-2xl p-3"
                style={{ border: "1px solid #EEEEEE" }}
              >
                <p className="text-xs font-bold" style={{ color: "#333333" }}>
                  공지사항이 표시되는 영역입니다.
                </p>
                <p
                  className="text-xs mt-1 line-clamp-2"
                  style={{ color: "#666666" }}
                >
                  실제 공지사항 내용이 이 자리에 나타납니다. 광고 미리보기에서는
                  샘플로 표시됩니다.
                </p>
              </div>
            </div>

            <div className="flex-1" />

            {visibleBoxes.length > 0 && (
              <div className="px-3 pt-3 pb-4">
                <div className="grid grid-cols-3 gap-2">
                  {visibleBoxes.map((adver, i) => (
                    <PreviewBoxCard key={adver.id} adver={adver} index={i} />
                  ))}
                </div>
              </div>
            )}

            {visibleBanners.length === 0 && visibleBoxes.length === 0 && (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <i className="fas fa-ad text-4xl mb-3 text-gray-200"></i>
                  <p className="text-sm">활성화된 광고가 없습니다</p>
                </div>
              </div>
            )}

            <div
              className="sticky bottom-0 flex items-center justify-around py-2 bg-white"
              style={{ borderTop: "1px solid #EEEEEE" }}
            >
              {[
                { icon: "fa-home", label: "홈" },
                { icon: "fa-store", label: "마켓" },
                { icon: "fa-bell", label: "알림" },
                { icon: "fa-user", label: "마이" },
              ].map((tab) => (
                <div
                  key={tab.label}
                  className="flex flex-col items-center gap-0.5"
                >
                  <i
                    className={`fas ${tab.icon} text-base`}
                    style={{
                      color: tab.label === "홈" ? "#5B21B6" : "#BBBBBB",
                    }}
                  />
                  <span
                    className="text-[9px] font-medium"
                    style={{
                      color: tab.label === "홈" ? "#5B21B6" : "#BBBBBB",
                    }}
                  >
                    {tab.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-3 flex gap-3 justify-center text-xs text-white/70">
          <span>
            <i className="fas fa-images mr-1"></i>배너 {visibleBanners.length}개
          </span>
          <span>
            <i className="fas fa-th-large mr-1"></i>박스 {visibleBoxes.length}개
          </span>
          {banners.length + boxes.length >
            visibleBanners.length + visibleBoxes.length && (
            <span className="text-yellow-300">
              <i className="fas fa-eye-slash mr-1"></i>
              비활성{" "}
              {banners.length +
                boxes.length -
                (visibleBanners.length + visibleBoxes.length)}
              개 제외
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdverList() {
  const [searchTerm, setSearchTerm] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const {
    data: advers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["advers"],
    queryFn: adverService.getAllAdvers,
  });

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert />;

  const sortAdvers = (list: Adver[]) =>
    [...list].sort((a, b) =>
      a.sortOrder !== b.sortOrder
        ? a.sortOrder - b.sortOrder
        : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    );

  const allBanners = sortAdvers(
    (advers || []).filter((a) => a.adType === "BANNER"),
  );
  const allBoxes = sortAdvers((advers || []).filter((a) => a.adType === "BOX"));
  const totalCount = advers?.length || 0;
  const activeCount = advers?.filter((a) => a.isVisible).length || 0;
  const inactiveCount = advers?.filter((a) => !a.isVisible).length || 0;

  const filtered =
    advers?.filter((a) =>
      a.title.toLowerCase().includes(searchTerm.toLowerCase()),
    ) || [];
  const banners = sortAdvers(filtered.filter((a) => a.adType === "BANNER"));
  const boxes = sortAdvers(filtered.filter((a) => a.adType === "BOX"));

  return (
    <div>
      {showPreview && (
        <HomePreviewModal
          banners={allBanners}
          boxes={allBoxes}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">광고 관리</h1>
          <p className="text-gray-500 mt-2">앱 및 웹뷰 광고를 관리합니다</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreview(true)}
            className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm flex items-center space-x-2"
          >
            <i className="fas fa-mobile-alt text-primary"></i>
            <span>미리보기</span>
          </button>
          <Link
            to="/admin/adver/new"
            className="px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all shadow-md flex items-center space-x-2"
          >
            <i className="fas fa-plus"></i>
            <span>광고 등록</span>
          </Link>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <StatsCard
          label="전체 광고"
          value={totalCount}
          icon="fas fa-ad"
          borderColor="border-primary"
          iconColor="text-primary"
          bgColor="bg-primary/10"
        />
        <StatsCard
          label="상단 배너"
          value={allBanners.length}
          icon="fas fa-images"
          borderColor="border-marker-orange"
          iconColor="text-marker-orange"
          bgColor="bg-marker-orange/10"
          valueColor="text-marker-orange"
        />
        <StatsCard
          label="하단 박스"
          value={`${allBoxes.length} / 3`}
          icon="fas fa-th-large"
          borderColor="border-purple-500"
          iconColor="text-purple-500"
          bgColor="bg-purple-500/10"
          valueColor="text-purple-600"
        />
        <StatsCard
          label="활성 광고"
          value={activeCount}
          icon="fas fa-check-circle"
          borderColor="border-green-500"
          iconColor="text-green-500"
          bgColor="bg-green-500/10"
          valueColor="text-green-600"
        />
        <StatsCard
          label="비활성 광고"
          value={inactiveCount}
          icon="fas fa-pause-circle"
          borderColor="border-gray-400"
          iconColor="text-gray-400"
          bgColor="bg-gray-400/10"
          valueColor="text-gray-600"
        />
      </div>

      {/* Search Bar */}
      <div className="mb-6 bg-white rounded-xl shadow-md p-6">
        <div className="relative">
          <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          <input
            type="text"
            placeholder="광고 제목으로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary-light outline-none transition-all"
          />
        </div>
      </div>

      {/* 상단 배너 섹션 */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
            <i className="fas fa-images text-primary"></i>
            <span className="font-bold text-primary">상단 배너</span>
          </div>
          <span className="text-sm text-gray-400">{banners.length}개</span>
        </div>
        {banners.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {banners.map((adver, i) => {
              const isDuplicate = banners.some(
                (b, j) => j !== i && b.sortOrder === adver.sortOrder,
              );
              return (
                <AdverCard
                  key={adver.id}
                  adver={adver}
                  rank={i + 1}
                  isDuplicate={isDuplicate}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
            <i className="fas fa-images text-4xl mb-3 text-gray-200"></i>
            <p>
              {searchTerm
                ? "검색 결과가 없습니다"
                : "등록된 상단 배너가 없습니다"}
            </p>
          </div>
        )}
      </div>

      {/* 하단 박스 섹션 */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-marker-orange/10 rounded-lg">
            <i className="fas fa-th-large text-marker-orange"></i>
            <span className="font-bold text-marker-orange">하단 박스</span>
          </div>
          <span className="text-sm text-gray-400">{boxes.length} / 3개</span>
        </div>
        {boxes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {boxes.map((adver, i) => {
              const isDuplicate = boxes.some(
                (b, j) => j !== i && b.sortOrder === adver.sortOrder,
              );
              return (
                <AdverCard
                  key={adver.id}
                  adver={adver}
                  rank={i + 1}
                  isDuplicate={isDuplicate}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-400">
            <i className="fas fa-th-large text-4xl mb-3 text-gray-200"></i>
            <p>
              {searchTerm
                ? "검색 결과가 없습니다"
                : "등록된 하단 박스 광고가 없습니다"}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
